// components/PhotoCapture.jsx - Photo Capture with Compression
import React, { useRef } from 'react'
import { Camera, X, Image as ImageIcon, Plus } from 'lucide-react'
import imageCompression from 'browser-image-compression'

const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5, // Max 500KB
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  fileType: 'image/jpeg'
}

export default function PhotoCapture({ photos, onChange }) {
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)
  
  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || [])
    
    if (files.length === 0) return
    
    // Process each file
    const newPhotos = await Promise.all(
      files.map(async (file) => {
        try {
          // Compress the image
          const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS)
          
          // Create preview URL
          const previewUrl = URL.createObjectURL(compressedFile)
          
          return {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            file: compressedFile,
            preview: previewUrl,
            name: file.name,
            size: compressedFile.size
          }
        } catch (error) {
          console.error('Failed to compress image:', error)
          // Fall back to original file
          return {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            size: file.size
          }
        }
      })
    )
    
    onChange([...photos, ...newPhotos])
    
    // Reset input
    e.target.value = ''
  }
  
  // Remove a photo
  const removePhoto = (photoId) => {
    const photo = photos.find(p => p.id === photoId)
    if (photo?.preview) {
      URL.revokeObjectURL(photo.preview)
    }
    onChange(photos.filter(p => p.id !== photoId))
  }
  
  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  
  return (
    <div className="p-4 space-y-4">
      <p className="text-sm text-gray-600">
        Add photos of the application area, equipment, or product labels (optional)
      </p>
      
      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo) => (
            <div key={photo.id} className="relative aspect-square">
              <img
                src={photo.preview}
                alt={photo.name}
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                onClick={() => removePhoto(photo.id)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full
                           flex items-center justify-center shadow-md"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white 
                              text-xs px-2 py-1 rounded-b-lg truncate">
                {formatSize(photo.size)}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Add photo buttons */}
      <div className="grid grid-cols-2 gap-3">
        {/* Camera button */}
        <button
          onClick={() => cameraInputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 p-6 
                     border-2 border-dashed border-gray-300 rounded-xl
                     hover:border-arbor-400 hover:bg-arbor-50 transition-colors"
        >
          <Camera className="w-8 h-8 text-gray-400" />
          <span className="text-sm text-gray-600">Take Photo</span>
        </button>
        
        {/* Gallery button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 p-6 
                     border-2 border-dashed border-gray-300 rounded-xl
                     hover:border-arbor-400 hover:bg-arbor-50 transition-colors"
        >
          <ImageIcon className="w-8 h-8 text-gray-400" />
          <span className="text-sm text-gray-600">From Gallery</span>
        </button>
      </div>
      
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {/* Photo count info */}
      <div className="text-center text-sm text-gray-500">
        {photos.length === 0 ? (
          <p>No photos attached</p>
        ) : (
          <p>{photos.length} photo{photos.length !== 1 ? 's' : ''} • Photos will be uploaded to Google Drive</p>
        )}
        {photos.length >= 10 && (
          <p className="text-yellow-600 mt-1">Maximum 10 photos per submission</p>
        )}
      </div>
    </div>
  )
}
